import { Controller } from "@hotwired/stimulus"

// Cola de revisión: mantiene al día los contadores (total, por estado y por
// categoría) cuando el servidor reemplaza una fila por stream tras guardarla.
// Se calcula la diferencia entre la fila vieja y la nueva a partir de su
// color de estado (la misma clase que pinta `ranking_price_color`), así no
// hace falta otra consulta por cada guardado. El número exacto se recupera
// al recargar; esto solo evita que la cabecera mienta mientras se trabaja.
//
// Uso: data-controller="review-queue" en el contenedor; cada contador lleva
// data-review-queue-count="total" | "state-<estado>" | "cat-<id>" |
// "cat-<id>-<estado>".
const STATE_BY_CLASS = {
  "table-warning": "new_prices",
  "table-info": "filled_by_ai",
  "table-secondary": "unreviewed"
}

export default class extends Controller {
  connect() {
    this.onStreamRender = (event) => this.beforeStreamRender(event)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  beforeStreamRender(event) {
    const stream = event.target
    if (stream.getAttribute("action") !== "replace") return
    const target = stream.getAttribute("target") || ""
    if (!target.startsWith("tr-ranking-price-")) return

    const oldRow = document.getElementById(target)
    if (!oldRow || !this.element.contains(oldRow)) return

    const before = this.stateOf(oldRow)
    const categoryId = this.categoryOf(oldRow)
    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      const after = this.stateOf(document.getElementById(target))
      if (before !== after) this.apply(categoryId, before, after)
    }
  }

  stateOf(row) {
    if (!row) return null
    for (const [klass, state] of Object.entries(STATE_BY_CLASS)) {
      if (row.classList.contains(klass)) return state
    }
    return null
  }

  categoryOf(row) {
    const item = row.closest("[id^='review-item-']")
    return item ? item.id.replace("review-item-", "") : null
  }

  apply(categoryId, before, after) {
    if (before) this.bump(categoryId, before, -1)
    if (after) this.bump(categoryId, after, +1)
  }

  bump(categoryId, state, delta) {
    this.add("total", delta)
    this.add(`state-${state}`, delta)
    if (categoryId) {
      this.add(`cat-${categoryId}`, delta)
      this.add(`cat-${categoryId}-${state}`, delta)
    }
  }

  add(key, delta) {
    this.element.querySelectorAll(`[data-review-queue-count="${key}"]`).forEach((el) => {
      const value = Math.max(0, (parseInt(el.textContent, 10) || 0) + delta)
      el.textContent = value
      el.closest(".stat-chip")?.classList.toggle("is-zero", value === 0)
    })
  }
}
